import { Box, CircularProgress } from '@mui/material'
import { useBreadcrumbsData } from 'Common/Breadcrumbs/Breadcrumbs.data'
import { Page } from 'Common/Page/Page'
import { useNavigate, useParams } from 'react-router-dom'
import { Button, PageHeader } from 'Common/Supplyvue-ui'
import {
  AlternativeWorkcentrePageURLTypes,
  AlternativeWorkcentreTableRowType,
} from './AlternativeWorkcenters.types'
import { AlternativeWorkcentersMUITable } from './AlternativeWorkcenters.MUITable'
import {
  useAlternativeWorkcentreData,
  useUpdateAlternativeWorkcentreData,
  useWorkcentreRunStatus,
} from './AlternativeWorkcenters.data'
import { useRef, useState, useEffect } from 'react'
import { toast } from 'react-hot-toast'
import { WORKCENTRE_STATUS } from 'Common/Constants/Constants'
import { getApiErrorMessage } from 'Features/auth/axios-client'
import { usePlanBuiltByV2Engine } from 'Common/Charts/StockWaterfall/stockWaterfall.data'

export const AlternativeWorkcentersPage = () => {
  const navigate = useNavigate()
  const { workcentreId, planId, sourcesiteId } =
    useParams<AlternativeWorkcentrePageURLTypes>()

  const [isEdited, setIsEdited] = useState(false)
  const [isAnyWorkcentreRunning, setIsAnyWorkcentreRunning] = useState(false)
  const isAnyWorkcentreRunningRef = useRef(false)

  useBreadcrumbsData({
    plan_id: planId,
    workcentre_id: workcentreId,
  })

  const dataToBeUpdated = useRef<
    Record<number, AlternativeWorkcentreTableRowType>
  >({})

  const {
    mutate: updateAlternativeWorkcentreData,
    isLoading: isUpdatingAlternativeWorkcentreData,
  } = useUpdateAlternativeWorkcentreData({
    onSuccess: () => {
      toast.success('Data updated successfully')
      navigate(`/plan/${planId}/`)
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error, 'Error updating data'))
    },
  })
  const { builtByV2Engine } = usePlanBuiltByV2Engine(planId)
  const setDataTobeUpdated = (
    data: Record<number, AlternativeWorkcentreTableRowType>
  ) => {
    dataToBeUpdated.current = {
      ...dataToBeUpdated.current,
      ...data,
    }
    setIsEdited(true)
  }

  const {
    data: alternativeWorkcentreData,
    isLoading: isAlternativeWorkcentreDataLoading,
  } = useAlternativeWorkcentreData(planId, workcentreId, sourcesiteId)

  const { data: workcentreRunStatus, isLoading: workCentreRunStatusLoading } =
    useWorkcentreRunStatus({ planId, sourcesiteId, workcentreId })

  useEffect(() => {
    let intervalId: NodeJS.Timeout
    setIsAnyWorkcentreRunning(
      workcentreRunStatus?.is_any_workcentre_being_run ?? false
    )
    if (
      workcentreRunStatus &&
      (workcentreRunStatus?.status === WORKCENTRE_STATUS.INITIATED ||
        workcentreRunStatus?.status === WORKCENTRE_STATUS.RUNNING)
    ) {
      intervalId = setTimeout(() => {
        navigate(`/plan/${planId}`)
      }, 1000)

      return () => {
        clearInterval(intervalId)
      }
    }
  }, [workcentreRunStatus, history, planId])

  useEffect(() => {
    if (isAnyWorkcentreRunning) {
      isAnyWorkcentreRunningRef.current = true
      toast.error(
        'Another workcentre is being run. Check back later to edit plan for this workcentre.',
        {
          duration: 10000 * 60,
        }
      )
    } else if (
      isAnyWorkcentreRunning === false &&
      isAnyWorkcentreRunningRef.current === true
    ) {
      isAnyWorkcentreRunningRef.current = false
      toast.dismiss()
      toast.success(
        'Other workcentre finished running. Now you can save your changes.'
      )
    }
  }, [isAnyWorkcentreRunning])

  const handleApplyChangesButtonClick = () => {
    updateAlternativeWorkcentreData({
      data: dataToBeUpdated.current,
      planId,
      workcentreId,
      sourcesiteId,
    })
  }

  return (
    <Page>
      <Box display="flex" flexDirection="column" flex={1}>
        <PageHeader title="Alternative workcentre" />
        <Box>
          <AlternativeWorkcentersMUITable
            data={alternativeWorkcentreData?.table_data ?? []}
            isLoading={isAlternativeWorkcentreDataLoading}
            updateTableData={setDataTobeUpdated}
            primaryWorkCentreCodes={
              alternativeWorkcentreData?.primary_workcentre_codes
            }
            skuCodes={alternativeWorkcentreData?.sku_codes}
            workCentreCodes={alternativeWorkcentreData?.workcentre_codes}
          />
        </Box>
        <Box>
          <Box
            marginLeft={2}
            display="flex"
            sx={{
              marginTop: 1,
              justifyContent: 'flex-end',
            }}
          >
            <Button
              variant="contained"
              color="primary"
              onClick={handleApplyChangesButtonClick}
              disabled={
                !isEdited ||
                isUpdatingAlternativeWorkcentreData ||
                isAnyWorkcentreRunning ||
                builtByV2Engine
              }
            >
              {isUpdatingAlternativeWorkcentreData && (
                <CircularProgress color="inherit" size={16} />
              )}
              Apply changes
            </Button>
          </Box>
        </Box>
      </Box>
    </Page>
  )
}
